// The component guide a model reads (MCP resource `minne://guide/components`) and its summary
// (MCP server instructions). The component sections, examples and props are generated from
// COMPONENT_META and the zod schemas (see schemas.ts and props.ts), so the guide and the web
// docs page can never drift from what the server validates.
import { COMPONENT_META, COMPONENT_NAMES, COMPONENT_SCHEMAS } from "./schemas.js";
import { propsTable, rootProp } from "./props.js";
const BT = "```";
const BT4 = "````";
const METAS = COMPONENT_NAMES.map((n) => COMPONENT_META[n]);
function namesByBodyKind(kind) {
    return METAS.filter((m) => m.body === kind)
        .map((m) => `\`${m.name}\``)
        .join(", ");
}
function documentedRows(meta) {
    const derived = meta.derivedProps ?? [];
    return propsTable(COMPONENT_SCHEMAS[meta.name]).filter((r) => !derived.includes(rootProp(r.name)));
}
/** Props a reader writes (schema-derived body props hidden), as guide prose. */
function propsSentence(meta) {
    const rows = documentedRows(meta);
    if (!rows.length)
        return "";
    return (rows
        .map((r) => `\`${r.name}\`: ${r.type}${r.default !== undefined ? ` (default ${r.default})` : r.required ? "" : " (optional)"}`)
        .join(". ") + ".");
}
function componentSection(meta) {
    const props = propsSentence(meta);
    const extras = [meta.bodyNote, props, meta.hint].filter(Boolean).join(" ");
    return [`### ${meta.name} — ${meta.summary}`, extras, ...meta.examples].join("\n");
}
export const COMPONENT_GUIDE = `# Minne component guide

A note is GFM Markdown. Plain text, lists, links and \`[[Wikilinks]]\` render as clean typography;
raw HTML is never rendered. Richer content is a *component*: a fenced code block whose info string
starts with a component name. Use the smallest thing that works: prose plus one or two components.

## Info-string rule

\`${BT}name id=x key=value key="value with spaces"\` — the name first, then \`key=value\` pairs.
Double-quote values with spaces (escape quotes as \\"). \`id\` is always allowed.

- **Text components** (${namesByBodyKind("text")}): content in the body,
  scalar props in the info string.
- **Structured components** (${namesByBodyKind("yaml")}): YAML in the
  body. Info-string scalars are merged in (body wins).
- ${namesByBodyKind("nested")}: a four-backtick fence whose body is Markdown with nested blocks.

Unknown names, bad YAML or invalid props do not fail the write: the block renders as a plain
code block and the result lists the error; fix it with \`replace_block\`.

## Components

${METAS.map(componentSection).join("\n\n")}

## Block ids

Every heading and component gets a stable short id on write: headings as a trailing \`{#h2}\`,
components as \`id=c1\` in the info string. The server adds missing ids, keeps valid ones you
supply (\`^[a-z][a-z0-9_-]{0,31}$\`) and renames duplicates; write results list the ids. Keep the
ids when you rewrite a block so links and patches stay stable. Ordinary paragraphs and lists have
no id: change them with \`find_replace\` or \`replace_section\`.

## Patching (patch_note ops)

Ops apply in order against the note \`version\` you read; a stale version returns the current text.

| op | fields | effect |
|---|---|---|
| append | markdown | add blocks at the end |
| replace_block | block_id, markdown | replace a heading or component (keep its id) |
| insert_after / insert_before | block_id, markdown | add blocks next to one |
| delete_block | block_id | remove a heading line or component |
| replace_section | heading_id, markdown | keep the heading, replace everything under it up to the next heading of the same or higher level |
| find_replace | find, replace, all? | literal text; must match exactly once unless all: true |
| set_title | title | rewrite the first H1 (created if missing) |
| toggle_check | block_id, index, checked | tick/untick the n-th item (0-based) of a checklist |

Example: \`{"op":"toggle_check","block_id":"k1","index":1,"checked":true}\`.

## Writing well

Lead with a short summary; use H2/H3 headings so \`read_note\` outlines are useful. Prose for
explanations, \`checklist\` for next steps, \`decision\` for choices, \`table\`/\`cards\` for comparisons,
\`callout\` sparingly. Link notes with \`[[Note title]]\` (they become backlinks). Variants come from
the fixed lists (tone, status, type); there is no custom styling.
`;
function summaryLine(meta) {
    // Top-level rows only; nested enum values ride along inside the inline object types. The
    // hint carries rules the types cannot show (decision.chosen is an option id, stats aliases).
    const rows = documentedRows(meta).filter((r) => r.depth === 0);
    const props = rows.map((r) => `${r.name}${r.required ? "" : "?"}: ${r.type.replaceAll(" | ", "|")}`).join(", ");
    return `- ${meta.name} (${meta.body}) — ${[props, meta.summary, meta.hint].filter(Boolean).join(" — ")}`;
}
export const COMPONENT_GUIDE_SUMMARY = `Minne stores notes as GFM Markdown with optional component blocks: fenced code blocks whose info
string starts with a component name, then key=value pairs (quote values with spaces).
Raw HTML is never rendered. Read the resource minne://guide/components for the full guide.

Components:
${METAS.map(summaryLine).join("\n")}

Invalid blocks do not fail a write: they render as code and the result lists the error to fix.
Every heading ({#h1}) and component (id=c1) gets a stable id on write; write results list them.
Paragraphs have no id: edit them with find_replace or replace_section.
patch_note ops: append, replace_block, insert_after, insert_before, delete_block, replace_section,
find_replace (exactly one match unless all: true), set_title, toggle_check (index is 0-based).
Pass the note version you read; a stale version returns the current text. Pass thread=<id> on
later calls in the same conversation so the notes you touch are grouped.`;
//# sourceMappingURL=guide.js.map